/**
 * R8 Alert rules tab — the alert-rule BLOCKED-exclusion affordance.
 *
 * Honesty rail (CD R8): engine-level default is "BLOCKED excluded from
 * source.state alert matching." The toggle surfaces the choice rather
 * than hiding it — operators can opt a specific blocked source back in
 * if they're actively chasing a workaround.
 */

import { useState } from 'react';
import { ReasonTag } from './primitives';
import { SAMPLE_SOURCES } from './sample-data';

export function AlertExclusion() {
  const [exclude, setExclude] = useState(true);
  const blocked = SAMPLE_SOURCES.filter(s => s.state === 'blocked' && s.reason);

  return (
    <div>
      <section style={{
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        overflow: 'hidden',
      }}>
        <header style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--bm-border)',
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700,
          letterSpacing: '.4px', color: 'var(--bm-text)',
        }}>Starter rule · "Any source DEGRADED &gt; 2× cadence"</header>

        <div style={{ padding: 14 }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5,
            marginBottom: 14,
          }}>
            <span style={KEYWORD_STYLE}>WHEN</span>
            <Cond>source.state <b>=</b> DEGRADED</Cond>
            <Cond>age <b>&gt;</b> 2× cadence</Cond>
            <span style={KEYWORD_STYLE}>THEN</span>
            <span style={{
              padding: '2px 8px', borderRadius: 4,
              background: 'var(--bm-accent, #6ea3ff)', color: 'var(--bm-bg)',
              fontWeight: 700,
            }}>inbox</span>
          </div>

          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px',
            background: 'var(--bm-bg)',
            border: '1px solid var(--bm-border)',
            borderRadius: 'var(--bm-radius-sm)', cursor: 'pointer',
            marginBottom: 8,
          }}>
            <input
              type="checkbox" checked={exclude}
              onChange={e => setExclude(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>
              <b>Exclude structurally-blocked sources</b>
              <span style={{ color: 'var(--bm-text-subtle)' }}> (default on)</span>
            </span>
          </label>

          <div style={{
            padding: '10px 12px',
            fontSize: 12, lineHeight: 1.5,
            background: exclude ? 'var(--bm-state-live-bg)' : 'var(--bm-state-degraded-bg)',
            border: `1px solid ${exclude ? 'var(--bm-state-live)' : 'var(--bm-state-degraded)'}55`,
            borderRadius: 'var(--bm-radius-sm)',
            color: 'var(--bm-text)',
          }}>
            {exclude ? (
              <><b style={{ color: 'var(--bm-state-live)' }}>✓ {blocked.length} blocked sources excluded.</b>{' '}
                The rule won't fire on QLD / Iran / KCDC / NHS — they're known structural gaps, not signal. No alert fatigue.</>
            ) : (
              <><b style={{ color: 'var(--bm-state-degraded)' }}>⚠ {blocked.length} blocked sources included.</b>{' '}
                This rule will fire <b>forever</b> on every structurally-blocked source — permanent alert fatigue. Not recommended.</>
            )}
          </div>
        </div>

        <div style={{
          padding: '10px 14px',
          borderTop: '1px solid var(--bm-border)',
        }}>
          <div style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
            letterSpacing: '.6px', textTransform: 'uppercase',
            color: 'var(--bm-text-subtle)', marginBottom: 6,
          }}>Would-be-excluded sources</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {blocked.map(s => (
              <span key={s.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '3px 8px',
                fontSize: 11.5,
                background: 'var(--bm-bg)',
                border: '1px solid var(--bm-border)',
                borderRadius: 4,
              }}>
                <span style={{
                  opacity: exclude ? 0.5 : 1,
                  textDecoration: exclude ? 'line-through' : 'none',
                }}>{s.name}</span>
                {s.reason && <ReasonTag reason={s.reason} />}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{
        marginTop: 10, padding: '10px 14px',
        fontSize: 12, color: 'var(--bm-text-subtle)',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        Engine-level default: BLOCKED is excluded from <code>source.state</code> alert matching.
        The toggle surfaces the choice rather than hiding it — operators can opt a specific blocked source back in if they're actively chasing a workaround.
      </div>
    </div>
  );
}

const KEYWORD_STYLE = {
  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
  letterSpacing: '.4px',
  color: 'var(--bm-text-subtle)',
  padding: '2px 6px',
  background: 'var(--bm-bg)',
  border: '1px solid var(--bm-border)',
  borderRadius: 3,
};

function Cond({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      padding: '3px 8px',
      background: 'var(--bm-bg)',
      border: '1px solid var(--bm-border)',
      borderRadius: 4,
      color: 'var(--bm-text)',
    }}>{children}</span>
  );
}
